/**
 * API Service for fetching content from the backend
 */

const PRODUCTION_API = 'https://iouuye2b3b.execute-api.eu-west-1.amazonaws.com/api/v1';
const LOCAL_API = 'http://localhost:8000/api/v1';

// Use REACT_APP_API_URL if set, otherwise default to production
// For local development, create a .env.local file with REACT_APP_API_URL=http://localhost:8000/api/v1
const RAW_API_BASE = process.env.REACT_APP_API_URL || PRODUCTION_API;
const normalizeApiBase = (value) => {
  if (!value) return '';
  const trimmed = value.replace(/\/+$/, '');
  return trimmed.includes('/api/v1') ? trimmed : `${trimmed}/api/v1`;
};
const API_BASE = normalizeApiBase(RAW_API_BASE);

const ensureApiBase = () => {
  if (!API_BASE) {
    throw new Error('API base URL is not configured.');
  }
};

/**
 * Fetch all content from the API
 */
export async function fetchAllContent() {
  ensureApiBase();
  const response = await fetch(`${API_BASE}/content`);
  if (!response.ok) {
    throw new Error(`Failed to fetch content: ${response.status}`);
  }
  return response.json();
}

/**
 * Fetch content for a specific section
 */
export async function fetchSectionContent(sectionSlug) {
  ensureApiBase();
  const response = await fetch(`${API_BASE}/content/${sectionSlug}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch section: ${response.status}`);
  }
  return response.json();
}

/**
 * Transform API response to match existing data structure for DJ mode
 */
export function transformDJContent(apiData) {
  if (!apiData?.projects) return null;

  const projects = apiData.projects;
  const getProject = (slug) => projects.find(p => p.slug === slug);

  const hero = getProject('hero');
  const artist = getProject('artist');
  const sets = getProject('sets');
  const pressKit = getProject('press-kit');
  const contact = getProject('contact');
  const normalizeGigKey = (gig) => {
    if (gig?.title && gig?.content?.date) {
      return `${gig.title}__${gig.content.date}`;
    }
    if (gig?.slug) {
      return gig.slug.replace(/^gig-gig-/, 'gig-');
    }
    return gig?.id;
  };

  const mergeClips = (a = [], b = []) => {
    const seen = new Set();
    const merged = [];
    [...a, ...b].forEach((clip) => {
      if (!clip?.url) return;
      if (seen.has(clip.url)) return;
      seen.add(clip.url);
      merged.push(clip);
    });
    return merged;
  };

  const mergeTags = (a = [], b = []) => {
    const set = new Set([...a, ...b].filter(Boolean));
    return Array.from(set);
  };

  const gigProjects = projects.filter((p) => p.slug?.startsWith('gig-'));
  const gigMap = new Map();
  gigProjects.forEach((gig) => {
    const key = normalizeGigKey(gig);
    const existing = gigMap.get(key);
    if (!existing) {
      gigMap.set(key, gig);
      return;
    }

    const existingClips = existing.content?.clips || [];
    const currentClips = gig.content?.clips || [];
    const existingScore = existingClips.length + (existing.assets?.length || 0);
    const currentScore = currentClips.length + (gig.assets?.length || 0);

    const primary = currentScore > existingScore ? gig : existing;
    const secondary = primary === gig ? existing : gig;

    const merged = {
      ...primary,
      tags: mergeTags(primary.tags, secondary.tags),
      content: {
        ...primary.content,
        clips: mergeClips(primary.content?.clips || [], secondary.content?.clips || [])
      },
      thumbnail_url: primary.thumbnail_url || secondary.thumbnail_url,
      extra_data: primary.extra_data || secondary.extra_data
    };
    gigMap.set(key, merged);
  });

  const gigs = Array.from(gigMap.values()).sort(
    (a, b) => new Date(b.content?.date || 0) - new Date(a.content?.date || 0)
  );

  return {
    hero: hero ? {
      name: hero.title,
      badge: hero.subtitle,
      headline: hero.description,
      subheadline: hero.content?.subheadline,
      genres: hero.content?.genres,
      hero_image: hero.thumbnail_url?.replace('https://d1q048o59d0tgk.cloudfront.net/assets', ''),
      ctas: hero.content?.ctas || []
    } : null,
    artist: artist ? {
      title: artist.subtitle,
      bio: artist.description,
      artist_image: artist.thumbnail_url?.replace('https://d1q048o59d0tgk.cloudfront.net/assets', ''),
      highlights: artist.content?.highlights || []
    } : null,
    gigs: gigs.map(gig => ({
      id: gig.slug,
      event: gig.title,
      collective: gig.extra_data?.collective,
      date: gig.content?.date,
      location: gig.extra_data?.location,
      time: gig.content?.time,
      genre: gig.content?.genre || [],
      tags: gig.tags?.filter(t => !gig.content?.genre?.includes(t)) || [],
      description: gig.description,
      image: gig.thumbnail_url?.replace('https://d1q048o59d0tgk.cloudfront.net/assets', ''),
      clips: gig.content?.clips || []
    })),
    sets: sets ? {
      title: sets.title,
      description: sets.description,
      platforms: sets.content?.platforms || []
    } : null,
    pressKit: pressKit ? {
      title: pressKit.title,
      description: pressKit.description,
      bio_short: pressKit.content?.bio_short,
      bio_long: pressKit.content?.bio_long,
      technical_rider: pressKit.content?.technical_rider,
      downloads: pressKit.content?.downloads || [],
      gallery: pressKit.content?.gallery
    } : null,
    contact: contact ? {
      email: contact.content?.email,
      booking_title: contact.subtitle,
      booking_description: contact.description,
      social: contact.content?.social || {}
    } : null
  };
}

/**
 * Transform API response to match existing data structure for Professional mode
 */
export function transformProfessionalContent(apiData) {
  if (!apiData?.projects) return null;

  const projects = apiData.projects;
  const getProject = (slug) => projects.find(p => p.slug === slug);

  const hero = getProject('hero');
  const highlights = getProject('highlights');
  const about = getProject('about');
  const skills = getProject('skills');
  const certifications = getProject('certifications');
  const contact = getProject('contact');

  const education = projects
    .filter(p => p.extra_data?.type === 'education')
    .sort((a, b) => a.display_order - b.display_order);

  const experience = projects
    .filter(p => p.extra_data?.type === 'experience')
    .sort((a, b) => a.display_order - b.display_order);

  const featuredProjects = projects
    .filter(p => p.extra_data?.type === 'featured_project')
    .sort((a, b) => a.display_order - b.display_order);

  const githubProjects = projects
    .filter(p => p.extra_data?.type === 'github_project');

  // Group GitHub projects by category
  const githubByCategory = {};
  githubProjects.forEach(p => {
    const cat = p.extra_data?.category || 'other';
    if (!githubByCategory[cat]) githubByCategory[cat] = [];
    githubByCategory[cat].push({
      name: p.title,
      description: p.description,
      stack: p.content?.stack || [],
      repo: p.content?.repo,
      demo: p.content?.demo
    });
  });

  return {
    hero: hero ? {
      name: hero.title,
      title: hero.subtitle,
      headline: hero.description,
      subtext: hero.content?.subtext,
      headshot: hero.thumbnail_url?.replace('https://d1q048o59d0tgk.cloudfront.net/assets', ''),
      ctas: hero.content?.ctas || []
    } : null,
    highlights: highlights?.content?.items || [],
    about: about ? {
      title: about.title,
      paragraphs: about.content?.paragraphs || [about.description]
    } : null,
    education: education.map(edu => ({
      institution: edu.title,
      degree: edu.subtitle,
      location: edu.content?.location,
      dates: edu.content?.dates,
      status: edu.content?.status,
      description: edu.description,
      modules: edu.content?.modules || [],
      projects: edu.content?.projects || [],
      thesis: edu.content?.thesis,
      leadership: edu.content?.leadership || []
    })),
    experience: experience.map(exp => ({
      company: exp.title,
      role: exp.subtitle,
      location: exp.content?.location,
      dates: exp.content?.dates,
      duration: exp.content?.duration,
      responsibilities: exp.content?.responsibilities || [],
      achievements: exp.content?.achievements || []
    })),
    skills: skills ? {
      categories: skills.content?.categories || []
    } : { categories: [] },
    certifications: certifications?.content?.items || [],
    contact: contact?.content || {}
  };
}

/**
 * Transform API response for projects data
 */
const MONTH_YEAR_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  year: 'numeric',
});

const isYearMonth = (value = '') => /^\d{4}-\d{2}$/.test(value);

const yearMonthToLabel = (value = '') => {
  if (!isYearMonth(value)) return '';
  const [year, month] = value.split('-').map(Number);
  if (!year || !month || month < 1 || month > 12) return '';
  return MONTH_YEAR_FORMATTER.format(new Date(Date.UTC(year, month - 1, 1)));
};

const buildProjectTimeline = (startDate = '', endDate = '', legacyTimeline = '') => {
  const startLabel = yearMonthToLabel(startDate);
  const endLabel = yearMonthToLabel(endDate);
  if (startLabel && endLabel) return `${startLabel} – ${endLabel}`;
  if (startLabel || endLabel) return startLabel || endLabel;
  return typeof legacyTimeline === 'string' ? legacyTimeline.trim() : '';
};

export function transformProjectsContent(apiData) {
  if (!apiData?.projects) return null;

  const projects = apiData.projects;

  const featuredProjects = projects
    .filter(p => p.extra_data?.type === 'featured_project')
    .sort((a, b) => a.display_order - b.display_order);

  const githubProjects = projects
    .filter(p => p.extra_data?.type === 'github_project');

  // Group GitHub projects by category
  const githubByCategory = {};
  githubProjects.forEach(p => {
    const cat = p.extra_data?.category || 'other';
    if (!githubByCategory[cat]) githubByCategory[cat] = [];
    githubByCategory[cat].push({
      name: p.title,
      description: p.description,
      stack: p.content?.stack || [],
      repo: p.content?.repo,
      demo: p.content?.demo
    });
  });

  return {
    featured: featuredProjects.map((p) => {
      // Support both legacy flat content fields and the newer content.links shape.
      // This prevents old admin-saved records from losing link rendering.
      const content = p.content || {};
      const contentLinks = content.links || {};
      const rawMedia = Array.isArray(content.media)
        ? content.media
        : (Array.isArray(content.clips) ? content.clips : []);
      const media = rawMedia
        .map((item) => {
          if (!item) return null;
          if (typeof item === 'string') {
            return {
              type: /\.(mp4|mov|webm|m4v|ogg)$/i.test(item) ? 'video' : 'image',
              url: item
            };
          }
          if (typeof item === 'object' && item.url) {
            return {
              ...item,
              type: item.type || (/\.(mp4|mov|webm|m4v|ogg)$/i.test(item.url) ? 'video' : 'image'),
              url: item.url
            };
          }
          return null;
        })
        .filter(Boolean);
      const links = {
        github: contentLinks.github || content.github || null,
        demo: contentLinks.demo || content.demo || null,
        paper: contentLinks.paper || content.paper || content.research_paper || null,
        manual:
          contentLinks.manual ||
          contentLinks.artifact ||
          content.manual ||
          content.artifact ||
          content.configuration_manual ||
          null,
        artifact:
          contentLinks.artifact ||
          contentLinks.manual ||
          content.artifact ||
          content.configuration_manual ||
          content.manual ||
          null
      };

      return {
        id: p.id,
        title: p.title,
        category: p.extra_data?.category || p.subtitle,
        timeline: buildProjectTimeline(content.start_date, content.end_date, content.timeline),
        start_date: content.start_date || '',
        end_date: content.end_date || '',
        description: p.description,
        problem: content?.problem,
        approach: content?.approach,
        stack: Array.isArray(p.tags) && p.tags.length
          ? p.tags
          : (Array.isArray(content?.stack) ? content.stack : []),
        outcomes: content?.outcomes || [],
        links,
        demo_image: p.thumbnail_url,
        media
      };
    }),
    github_projects: githubByCategory
  };
}

export default {
  fetchAllContent,
  fetchSectionContent,
  transformDJContent,
  transformProfessionalContent,
  transformProjectsContent
};
